import { RestResponseGetInitialMarginFractionOverride } from '@katanaperps/katana-perps-sdk';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import {
  calculateUnrealizedPnLPercent,
  isPositionShort,
  renderLiquidationPrice,
} from '#/api/apiUtils';
import { ApiOrder, ApiPosition } from '#/api/types';
import RemoveIcon from '#/components/basic/icons/RemoveIcon';
import BasicTableStackable from '#/components/basic/table/BasicTableStackable';
import TextColored from '#/components/basic/text/TextColored';
import { isDashboardPath } from '#/components/trading/dashboardDetect';
import { ADLDescription } from '#/components/wallet/walletSharedTooltips';
import { TableCoinIcon } from '#/config/coinIcons/CoinIcon';
import { TPSL_ENABLED } from '#/config/constants';
import {
  formatPercentage,
  formatNumber,
  formatUsdWithSign,
} from '#/lib/display/formatting';
import { redirectTo } from '#/lib/redirect';
import { getDashboardUrl } from '#/reduxState/markets/listed/listedMarketsUtils';
import { prefillOrderInputQuantity } from '#/reduxState/orders/prefillOrderInputs';
import { staticDispatch, useSelector } from '#/reduxState/store';

import ADLIndicator from './ADLIndicator';
import PositionDetailPageMobile from './detail/PositionDetailPageMobile';
import PnLShareShortcut from './pnLShare/PnLShareShortcut';
import TPSLColumnWrap from './takeProfitStopLoss/TPSLColumnWrap';

const MarketColumn = styled.div`
  display: inline;
  cursor: pointer;
`;

function PositionsTable({
  vaultManagerWallet,
  marketFilter,
  positions,
  closeActions,
  tpSlActionsData,
  IMFOverridesData,
}: {
  vaultManagerWallet?: string;
  marketFilter: string | undefined;
  positions: ApiPosition[] | undefined;
  IMFOverridesData: RestResponseGetInitialMarginFractionOverride | undefined;
  closeActions:
    | {
        showCloseAllConfirmation: () => void;
        showClosePositionModal: (market: string) => void;
      }
    | false;
  tpSlActionsData:
    | {
        allOrders: ApiOrder[] | undefined;
        setEditTpSl: (market: string) => void;
      }
    | false;
}) {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const currentMarket = useSelector(state => state.markets.currentMarket);
  const isMobile = useSelector(state => state.screen.isMobile);
  const isTablet = useSelector(state => state.screen.isTablet);
  const tickSizes = useSelector(state => state.markets.listedMarkets.tickSizes);
  const stepSizes = useSelector(state => state.markets.listedMarkets.stepSizes);
  const marketsLeverageParams = useSelector(
    state => state.markets.listedMarkets.marketsLeverageParams
  );

  const IMFOverridesMap = useMemo(
    () =>
      IMFOverridesData?.reduce(
        (map, override) => {
          map[override.market] = override.initialMarginFractionOverride;
          return map;
        },
        {} as Record<string, string | null>
      ),
    [IMFOverridesData]
  );

  const { pathname } = useLocation();
  const isDashboard = isDashboardPath(pathname);

  const [mobileDetailMarket, setMobileDetailMarket] = useState<string>();

  const filteredPositions = marketFilter
    ? positions?.filter(position => position.market === marketFilter)
    : positions;

  if (mobileDetailMarket) {
    return (
      <PositionDetailPageMobile
        vaultManagerWallet={vaultManagerWallet}
        market={mobileDetailMarket}
        onClose={() => setMobileDetailMarket(undefined)}
      />
    );
  }

  return (
    <>
      <BasicTableStackable
        notFixedLayout
        errorText={
          !walletAddress && !vaultManagerWallet
            ? 'Connect wallet to see your positions'
            : !positions?.length
            ? 'No open positions'
            : !filteredPositions?.length && !!positions?.length
            ? 'No positions for selected market'
            : undefined
        }
        onRowClick={
          isMobile
            ? position => setMobileDetailMarket(position.market)
            : undefined
        }
        getItemKey={item => item.market}
        items={filteredPositions || []}
        stripes
        marginBottom="24px"
        firstColumnPaddingLeft="16px"
        columns={position => {
          const [base] = position?.market?.split('-') || [];
          const {
            market,
            quantity,
            value,
            entryPrice,
            indexPrice,
            liquidationPrice,
            marginRequirement,
            unrealizedPnL,
            realizedPnL,
            adlQuintile,
          } = position || {};
          const unrealizedPnLNum = Number(unrealizedPnL);
          const realizedPnLNum = Number(realizedPnL);
          const unrealizedPnLPercent =
            !IMFOverridesMap || !market
              ? null
              : calculateUnrealizedPnLPercent(
                  position,
                  market && marketsLeverageParams
                    ? marketsLeverageParams[market]
                    : undefined,
                  market ? IMFOverridesMap[market] : null
                ) || '';
          const isShort = isPositionShort({ quantity: quantity || '0' });
          const tickSizeDecimals = tickSizes[market || '']?.visibleDecimals;
          const stepSizeDecimals = stepSizes[market || '']?.visibleDecimals;
          return [
            {
              name: 'Market',
              fontFamily: 'standard',
              render: (
                <MarketColumn
                  onClick={
                    isMobile
                      ? undefined
                      : () =>
                          redirectTo(getDashboardUrl('katanaEVMChain', market))
                  }
                >
                  <TableCoinIcon symbol={base} />
                  {market}
                </MarketColumn>
              ),
            },
            {
              name: 'Quantity',
              stackWithPrevious: isMobile || isTablet,
              onValueClick:
                isDashboard &&
                !isMobile &&
                currentMarket.pair === market &&
                position?.quantity
                  ? () =>
                      staticDispatch(
                        prefillOrderInputQuantity(
                          position?.quantity?.replace('-', '')
                        )
                      )
                  : undefined,
              render: (
                <TextColored color={isShort ? 'red' : 'green'}>
                  {formatNumber(quantity, { fixedDecimals: stepSizeDecimals })}{' '}
                  {base}
                </TextColored>
              ),
            },
            {
              name: 'Value',
              render: isMobile ? false : formatUsdWithSign(value),
            },
            {
              name: 'Entry Price',
              render: formatNumber(entryPrice, {
                fixedDecimals: tickSizeDecimals,
              }),
            },
            {
              name: 'Index Price',
              render: isMobile
                ? false
                : formatNumber(indexPrice, { fixedDecimals: tickSizeDecimals }),
            },
            {
              name: 'Liquidation Price',
              stackWithPrevious: isMobile,
              render: renderLiquidationPrice(
                liquidationPrice,
                tickSizeDecimals
              ),
            },
            {
              name: 'Position Margin',
              render: isMobile ? false : formatUsdWithSign(marginRequirement),
            },
            {
              name: 'Unrealized P&L (%)',
              align: isMobile ? 'right' : 'left',
              render: (
                <TextColored
                  color={
                    unrealizedPnLNum === 0
                      ? 'default'
                      : unrealizedPnLNum < 0
                      ? 'red'
                      : 'green'
                  }
                >
                  {formatUsdWithSign(unrealizedPnLNum)}{' '}
                  {unrealizedPnLPercent && (
                    <>({formatPercentage(unrealizedPnLPercent)}%)</>
                  )}
                  {market && position && (
                    <PnLShareShortcut market={market} position={position} />
                  )}
                </TextColored>
              ),
            },
            {
              name: 'Realized P&L',
              stackWithPrevious: isMobile || isTablet,
              render: (
                <TextColored color={realizedPnLNum < 0 ? 'red' : 'green'}>
                  {formatUsdWithSign(realizedPnLNum)}
                </TextColored>
              ),
            },
            // Column is not available on Vaults tabs
            {
              name: 'TP/SL',
              render:
                !TPSL_ENABLED || !tpSlActionsData
                  ? false
                  : isMobile
                  ? false
                  : position => (
                      <TPSLColumnWrap
                        allOrders={tpSlActionsData.allOrders}
                        market={position?.market}
                        tickSizeDecimals={tickSizeDecimals}
                        onEdit={() =>
                          tpSlActionsData.setEditTpSl(position.market)
                        }
                      />
                    ),
            },
            {
              name: 'ADL',
              tooltip: ADLDescription,
              render: isMobile ? (
                false
              ) : adlQuintile !== undefined ? (
                <ADLIndicator currentLiquidationChance={adlQuintile} />
              ) : (
                ''
              ),
            },
            // Column is not available on Vaults tabs
            {
              align: 'center',
              name: 'Close All',
              nameAsLink: true,
              fontFamily: 'standard',
              disabled: !filteredPositions?.length,
              onClick: closeActions
                ? closeActions.showCloseAllConfirmation
                : undefined,
              width: '25px',
              render:
                !closeActions || isMobile
                  ? false
                  : position => (
                      <RemoveIcon
                        onClick={() =>
                          closeActions.showClosePositionModal(position.market)
                        }
                      />
                    ),
            },
            // {
            //   name: '',
            //   width: EXPAND_COLUMN_WIDTH,
            //   render: isMobile ? <ExpandColumnIcon /> : false,
            // },
          ];
        }}
      />
    </>
  );
}

export default PositionsTable;
